import Agilite from 'agilite'
import CoreEnums from '../../../../core/utils/enums'
import SystemUsersEnums from './enums'
import store from '../../../../store'
import { getDataWithSync } from '../../../../db/dbSync'
import { STORES } from '../../../../db/indexedDB'
import { createLookup } from '../../../lib/utils'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const findUserByID = (recordId) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(
          SystemUsersEnums.profileKeys.AUTH_USERS,
          SystemUsersEnums.routeKeys.FIND_BY_ID,
          {
            recordId
          }
        )
        resolve(response.data)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const countSystemUsers = (qry) => {
  return new Promise((resolve, reject) => {
    let response = null
    let errorMessage = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(
          SystemUsersEnums.profileKeys.AUTH_USERS,
          SystemUsersEnums.routeKeys.COUNT,
          {
            filter: JSON.stringify({ ...qry, 'extraData.entities': { $in: [store.getState().core.entity._id] } })
          }
        )
        resolve(response.data.result)
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          errorMessage = e.response.data.errorMessage
        } else if (e.message) {
          errorMessage = e.message
        } else {
          errorMessage = CoreEnums.errorMessages.UNKNOWN_ERROR
        }

        reject(errorMessage)
      }
    })()
  })
}

export const readSystemUsersAggregate = (qry, page = null, pageLimit = null, projection, fetchAll, isPwa = false) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        let response = null
        // Build pipeline
        const pipeline = []

        // Always restrict to current entity unless explicitly fetching all
        if (!fetchAll) {
          const currentEntityId = store.getState().core?.entity?._id
          if (currentEntityId) {
            qry = { ...qry, 'extraData.entities': { $in: [currentEntityId] } }
          }
        }

        // Add initial match stage if query exists - this is critical for performance
        if (qry && Object.keys(qry).length > 0) {
          pipeline.push({ $match: qry })
        }

        // Add sorting before pagination to ensure consistent results
        pipeline.push({ $sort: { dateCreated: -1 } })

        // Add pagination early in the pipeline to reduce the number of documents processed
        if (page && pageLimit) {
          pipeline.push({ $skip: (page - 1) * pageLimit }, { $limit: parseInt(pageLimit) })
        }

        // Add main member lookup for dependants - only after pagination
        pipeline.push(
          // Convert string IDs to ObjectIds
          {
            $addFields: {
              mainMemberIdObjectId: {
                $cond: [
                  {
                    $or: [{ $eq: ['$mainMemberId', null] }, { $eq: ['$mainMemberId', ''] }]
                  },
                  null,
                  { $toObjectId: '$mainMemberId' }
                ]
              }
            }
          },

          // Main member lookup
          createLookup(
            `${process.env.REACT_APP_AGILITE_TEAM_ID}_auth_users`,
            'mainMemberIdObjectId',
            '_id',
            'mainMemberRecord',
            {
              profileImage: 0
            }
          ),

          // Unwind main member array
          { $unwind: { path: '$mainMemberRecord', preserveNullAndEmptyArrays: true } },

          // Project final fields
          {
            $project: {
              profileImage: 0
            }
          },

          {
            $project: {
              // Computed fields
              tmpPhoneNumber: { $ifNull: ['$mainMemberRecord.phoneNumber', null] },
              tmpEmail: { $ifNull: ['$mainMemberRecord.email', null] },
              mainMemberName: {
                $cond: [
                  { $ifNull: ['$mainMemberRecord', false] },
                  {
                    $concat: [
                      { $ifNull: ['$mainMemberRecord.firstName', ''] },
                      ' ',
                      { $ifNull: ['$mainMemberRecord.lastName', ''] }
                    ]
                  },
                  null
                ]
              },
              mainMemberIdNo: { $ifNull: ['$mainMemberRecord.idNo', null] },

              // Keep record object
              mainMemberRecord: 1,
              // Bring through all properties except profileImage
              _id: 1,
              firstName: 1,
              lastName: 1,
              email: 1,
              phoneNumber: 1,
              idNo: 1,
              dateCreated: 1,
              dateModified: 1,
              createdBy: 1,
              medicalAid: 1,
              modifiedBy: 1,
              isActive: 1,
              mainMemberId: 1,
              extraData: 1,
              residentialAddress: 1,
              professionalType: 1
            }
          }
        )

        // Execute aggregation
        if (isPwa) {
          response = await getDataWithSync(STORES.SYSTEM_USERS, {
            body: {
              pipeline: JSON.stringify(pipeline)
            },
            requestConfig: {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'profile-key': SystemUsersEnums.profileKeys.AUTH_USERS,
                'route-key': SystemUsersEnums.routeKeys.AGGREGATE
              }
            }
          })

          resolve(response)
        } else {
          response = await agilite.Connectors.execute(
            SystemUsersEnums.profileKeys.AUTH_USERS,
            SystemUsersEnums.routeKeys.AGGREGATE,
            { pipeline: JSON.stringify(pipeline) }
          )

          resolve(response.data)
        }
      } catch (e) {
        if (e.response?.data?.errorMessage) {
          reject(e.response.data.errorMessage)
        } else if (e.message) {
          reject(e.message)
        } else {
          reject(CoreEnums.errorMessages.UNKNOWN_ERROR)
        }
      }
    })()
  })
}
